<template>
  <NodeViewWrapper
    class="image"
    :class="{
      [node.attrs.mode]: true,
      placeholder: imageWidth < 30,
    }"
    :style="{ aspectRatio: `${node.attrs.width} / ${node.attrs.height}` }"
  >
    <div class="sizer" ref="sizer"></div>
    <pre v-if="node.attrs.error">
      Error: {{ node.attrs.error }}
    </pre>
    <img
      v-if="dataUrl"
      :src="dataUrl"
      class="in-progress"
      ref="localImage"
      @load="loaded"
    />
    <img v-if="node.attrs.href" :src="url" />
    <div class="overlays" v-if="editor.isEditable">
      <button @click="setMode('left')">Left</button>
      <button @click="setMode('wide')">Wide</button>
      <button @click="setMode('text-width')">Text Width</button>
      <button @click="setMode('full-width')">Full Width</button>
      <button @click="setMode('right')">Right</button>
    </div>
  </NodeViewWrapper>
</template>
<script lang="ts" setup>
import { NodeViewWrapper } from "@tiptap/vue-3";
import { type NodeViewProps } from "./types";

import {
  watchDebounced,
  useElementSize,
  useElementVisibility,
  useDevicePixelRatio,
} from "@vueuse/core";
import type { SupabaseClient } from "@supabase/supabase-js";

const props = defineProps<NodeViewProps>();
const sizer = ref();

const imageUploadStore = useImageUploadStore();
const dataUrl = ref();
const localImage = ref<HTMLImageElement>();
const client = useNuxtApp().$supabase?.client as SupabaseClient;

const { width } = useElementSize(sizer);
const intersecting = useElementVisibility(sizer);
const { pixelRatio } = useDevicePixelRatio();

watchDebounced(
  [width, intersecting],
  ([w, i]) => {
    // return;
    if (!i) return;
    if (w && w > imageWidth.value) {
      imageWidth.value = w * 1.1;
    }
  },
  { immediate: true }
);

type Mode = "text" | "text-width" | "full-width" | "left" | "right" | "wide";

function setMode(mode: Mode) {
  props.updateAttributes({ mode });
}

const imageWidth = ref(20);
const item = useBucketAndPath(() => props.node.attrs.href);

const url = computed(
  () =>
    (item.bucket &&
      item.path &&
      client.storage.from(item.bucket).getPublicUrl(item.path, {
        transform: {
          width: Math.round(imageWidth.value * pixelRatio.value),
          quality: 30,
          resize: "contain",
        },
      })?.data?.publicUrl) ??
    props.node.attrs.href
);

function loaded() {
  if (!localImage.value) throw new Error("No local image");

  props.updateAttributes({
    width: localImage.value.naturalWidth,
    height: localImage.value.naturalHeight,
  });
}

watch(
  () => imageUploadStore.value[props.node.attrs.uploadRef]?.url,
  (url) => {
    if (!url) return;
    props.updateAttributes({ href: url, uploadRef: null });
  },
  { immediate: true }
);

watch(
  () => imageUploadStore.value[props.node.attrs.uploadRef]?.file,
  (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = function (event) {
      dataUrl.value = event.target?.result;
    };
    reader.readAsDataURL(file);
  },
  { immediate: true }
);
</script>
<style scoped>
.image {
  display: grid;
  grid: auto / auto;
  grid-template-areas: "stack";
}

.image > * {
  grid-area: stack;
  align-self: stretch;
  justify-self: stretch;
  overflow: hidden;
}

.overlays {
  pointer-events: none;
}

.overlays > * {
  pointer-events: initial;
}

.in-progress {
  opacity: 0.5;
}
</style>
